import {
    useSyncExternalStore,
    useCallback,
    useContext,
    forwardRef,
    useEffect
} from "react";
import {
    TextInput as NativeTextInput,
    type TextInput,
    Animated,
    Platform,
    View
} from "react-native";
import type ICommandInputProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../../../types";
import {
    getAppearStyle,
    usePresence
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    CommandInputWebPropsType,
    CommandContextType
} from "../../type";
import {
    ChevronLeft as ChevronLeftIcon,
    Search as SearchIcon,
    X as XIcon
} from "lucide-react-native";
import {
    getPopoverSurfaceSearchSize
} from "../../../popoverSurface/components/search/stylesheet";
import {
    warnOnce
} from "../../../../utils/development";
import {
    COMMAND_SEARCH_SIZES
} from "../../stylesheet";
import {
    CommandContext
} from "../../context";
import AnimatedIcon from "../../../animatedIcon";
import Button from "../../../button";
import Chip from "../../../chip";

const CommandInputSearchIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <SearchIcon
    color={customColor}
    size={size}
/>;

const CommandInputClearIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={XIcon}
    size={size}
/>;

const CommandInputBackIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronLeftIcon}
    animation="signature"
    size={size}
/>;

const CommandInputContent: RefForwardingComponent<TextInput, ICommandInputProps & {
    context: CommandContextType;
}> = ({
    placeholder: placeholderProp,
    inputStyle,
    context,
    style,
    ...props
}, ref) => {
    const {
        customLocalize,
        customTheme,
        pageStack,
        variant
    } = context;

    const {
        controlPaddings,
        controlHeights,
        touchTargets,
        typography,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme, customLocalize);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const activeId = useSyncExternalStore(context.store.subscribe, context.store.getActiveId, context.store.getActiveId);

    const currentPage = pageStack[pageStack.length - 1];

    const previousPage = pageStack[pageStack.length - 2];

    const isLeadingButton = pageStack.length > 0 || variant === "sheet";

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isVisible: !!context.query,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear",
        customTheme
    });

    const currentSize = getPopoverSurfaceSearchSize({
        size: COMMAND_SEARCH_SIZES[variant],
        controlPaddings,
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes
    });

    const {
        container: containerDynamicStyle,
        action: actionDynamicStyle,
        crumbs: crumbsDynamicStyle,
        input: inputDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isLeadingButton,
        currentSize,
        pointerType,
        typography,
        borders,
        variant,
        colors,
        spaces
    });

    const handleInputRef = useCallback((node: TextInput | null) => {
        context.inputRef.current = node;

        if(typeof ref === "function") {
            ref(node);

            return;
        }

        if(ref) {
            ref.current = node;
        }
    }, [
        context.inputRef,
        ref
    ]);

    const handleClear = () => {
        context.onChangeText("");

        context.inputRef.current?.focus();
    };

    const handleLeadingPress = () => {
        if(pageStack.length) {
            context.onBack();

            context.inputRef.current?.focus();

            return;
        }

        context.onClose?.();
    };

    const webProps: CommandInputWebPropsType = Platform.OS === "web" ? {
        "aria-activedescendant": activeId,
        "aria-controls": context.listId,
        "aria-autocomplete": "list",
        "aria-haspopup": "listbox"
    } : {};

    const placeholder = currentPage ? currentPage.placeholder ?? `${localize("command-search-in", [
        currentPage.title
    ])}…` : placeholderProp ?? localize("search");

    const renderLeading = () => {
        if(isLeadingButton) {
            return <Button
                accessibilityLabel={pageStack.length ? localize("go-back-to", [
                    previousPage ? previousPage.title : localize("command-all-commands")
                ]) : localize("command-close-search")}
                size={currentSize.buttonSize}
                onPress={handleLeadingPress}
                icon={CommandInputBackIcon}
                customTheme={customTheme}
                spreadBehaviour="free"
                variant="ghost"
                type="neutral"
            />;
        }

        return <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <CommandInputSearchIcon
                customColor={colors.content.icon.mid}
                size={currentSize.iconSize}
                color="mid"
            />
        </View>;
    };

    const renderCrumbs = () => {
        if(!pageStack.length) {
            return null;
        }

        return <View
            style={[
                stylesheet.crumbs,
                crumbsDynamicStyle
            ]}
        >
            {
                pageStack.map((page) => <Chip
                    aria-label={localize("command-page-level", [
                        page.title
                    ])}
                    customTheme={customTheme}
                    title={page.title}
                    isClosable={false}
                    variant="tonal"
                    key={page.key}
                    type="neutral"
                    size="small"
                />)
            }
        </View>;
    };

    const renderClear = () => {
        if(!isRendered) {
            return null;
        }

        return <Animated.View
            style={[
                actionDynamicStyle,
                getAppearStyle({
                    isReduceMotionEnabled,
                    progress
                })
            ]}
        >
            <Button
                accessibilityLabel={localize("clear-search")}
                size={currentSize.buttonSize}
                icon={CommandInputClearIcon}
                customTheme={customTheme}
                spreadBehaviour="free"
                onPress={handleClear}
                variant="ghost"
                type="neutral"
            />
        </Animated.View>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderLeading()}
        {renderCrumbs()}
        <NativeTextInput
            autoFocus={variant !== "inline"}
            autoCapitalize="none"
            enterKeyHint="search"
            autoCorrect={false}
            inputMode="search"
            {...props}
            {...webProps}
            onSubmitEditing={Platform.OS === "web" ? undefined : context.onSubmit}
            aria-label={currentPage ? localize("command-search-in", [
                currentPage.title
            ]) : localize("command-input-label")}
            role={Platform.OS === "web" ? "combobox" : "searchbox"}
            placeholderTextColor={colors.content.text.low}
            underlineColorAndroid="transparent"
            onChangeText={context.onChangeText}
            onKeyPress={context.onKeyPress}
            placeholder={placeholder}
            submitBehavior="submit"
            style={[
                inputStyle,
                stylesheet.input,
                inputDynamicStyle
            ]}
            value={context.query}
            ref={handleInputRef}
            aria-expanded={true}
            multiline={false}
        />
        {renderClear()}
    </View>;
};

const CommandInputWithRef = forwardRef(CommandInputContent);

const CommandInput: RefForwardingComponent<TextInput, ICommandInputProps> = (props, ref) => {
    const context = useContext(CommandContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "Command.Input: must be rendered inside a Command."
        });
    }, [
        context
    ]);

    if(!context) {
        return null;
    }

    return <CommandInputWithRef
        {...props}
        context={context}
        ref={ref}
    />;
};
export default Object.assign(forwardRef(CommandInput), {
    commandPart: "input"
});
